'use client';

import React, { useEffect, useMemo, useState } from 'react';

import Image from 'next/image';
import Link from 'next/link';

import calendarIcon from '@/assets/icons/comissions-calendar-icon.svg';
import leftArrowIcon from '@/assets/icons/commissions-left-arrow-icon.svg';
import noData from '@/assets/icons/dashboard-no-commissions-icon.svg';
import cn from '@/helpers/utils/classNames';
import { extractRunIdFromDescription } from '@/helpers/utils/common';
import { useGetCommissionsDetailsMutation } from '@/store/api/dashboard';
import { MonthlyCommission } from '@/types/Dashboard';

import Button from '../Button';
import ReportsLoader from '../Reports/CustomerDetailModal/ReportsLoader';
import CustomSelect from '../Widgets/CustomSelect';
import LineChart from './Components/LineChart';

const MONTHLY = 'monthly';
const BI_MONTHLY = 'bi-monthly';

const getYears = (): { name: string; value: number }[] => {
  const currentYear = new Date().getFullYear();
  const years: { name: string; value: number }[] = [];

  for (let i = 0; i < 8; i++) {
    const year = currentYear - i;
    years.push({ name: year.toString(), value: year });
  }

  return years;
};

export type IMonthsWithRunId = { firstHalfRunId: string; secondHalfRunId: string };

const Commissions = () => {
  const [activeButton, setActiveButton] = useState<'monthly' | 'bi-monthly'>(MONTHLY);
  const [selectedYear, setSelectedYear] = useState(getYears()[0]?.value ?? '');

  const [getCommissionsDetails, { data, error, isLoading }] = useGetCommissionsDetailsMutation();

  useEffect(() => {
    if (selectedYear) {
      getCommissionsDetails({ selectedyear: selectedYear });
    }
  }, [selectedYear, getCommissionsDetails]);

  const calculateYAxis = (maxValue: number) => {
    const desiredBreakpoints = 4; // Show 4 labeled values including 0
    let yAxisMax: number;
    let step: number;

    if (maxValue <= 0) return { yAxisMax: 0, step: 0 };

    // Handle 10k-50k range with clean 10k increments
    if (maxValue > 10000 && maxValue <= 50000) {
      yAxisMax = Math.ceil(maxValue / 10000) * 10000; // Round up to nearest 10k
      step = yAxisMax / (desiredBreakpoints - 1); // Divide into 3 equal intervals
    } else {
      // Original logic for other ranges
      const order = Math.pow(10, Math.floor(Math.log10(maxValue)));
      step = Math.ceil(maxValue / (desiredBreakpoints * order)) * order;
      yAxisMax = Math.ceil(maxValue / step) * step;
    }

    return { yAxisMax, step };
  };

  const getBiMonthlyData = (data: any[]) => {
    const xLabels = new Set<string>();
    const firstHalfData = [];
    const secondHalfData = [];
    const monthsWithRunIds: IMonthsWithRunId[] = [];

    const sorted: MonthlyCommission[] = [...data].reverse();

    for (let i = 0; i < sorted.length - 1; i += 2) {
      const firstHalf = sorted[i];
      const secondHalf = sorted[i + 1];

      const month = new Date(firstHalf.period.startDate).toLocaleString('default', { month: 'short' });

      if (!xLabels.has(month)) {
        xLabels.add(month);
        firstHalfData.push(firstHalf.total);
        secondHalfData.push(secondHalf.total);
        monthsWithRunIds.push({
          firstHalfRunId: extractRunIdFromDescription(firstHalf.period.periodDescription),
          secondHalfRunId: extractRunIdFromDescription(secondHalf.period.periodDescription),
        });
      }
    }

    return { xLabels, firstHalfData, secondHalfData, monthsWithRunIds };
  };

  const chartData = useMemo(() => {
    let xLabels = new Set<string>();
    let uData: number[] = [];
    let pData: number[] = [];
    let monthsWithRunIds: IMonthsWithRunId[] = [];

    if (data) {
      if (activeButton === MONTHLY) {
        const sortedMonthly = [...(data.data?.monthlyCommissions ?? [])].sort(
          (a, b) => new Date(a.period.startDate).getTime() - new Date(b.period.startDate).getTime()
        );

        sortedMonthly.forEach((item: any) => {
          xLabels.add(new Date(item.period.startDate).toLocaleString('default', { month: 'short' }));
        });

        uData = sortedMonthly.map((item: any) => item.total) ?? [];
      } else {
        const generatedData = getBiMonthlyData(data?.data.biMonthlyCommission ?? []);
        xLabels = generatedData.xLabels;
        uData = generatedData.firstHalfData;
        pData = generatedData.secondHalfData;
        monthsWithRunIds = generatedData.monthsWithRunIds;
      }
    }

    const allValues = [...uData, ...pData];
    const maxYValue = Math.max(...allValues, 0); // Handle empty case
    const { yAxisMax, step } = calculateYAxis(maxYValue);

    return { xLabels, uData, pData, monthsWithRunIds, yAxisMax, yStep: step };
  }, [selectedYear, activeButton, data]);

  return (
    <div className="v2-py-6 v2-w-full v2-bg-white v2-rounded-xs v2-shadow-company-news-card">
      <div className="v2-px-4 md:v2-px-6">
        <div className="v2-flex v2-justify-between v2-items-center v2-gap-2">
          <h3 className="v2-font-sweet-sans-pro-bold v2-font-bold v2-text-xl v2-text-[#333333]">Commissions</h3>
          <div className="v2-flex v2-font-sweet-sans-pro v2-font-normal v2-text-xs md:v2-text-base v2-text-[#333333] v2-items-center">
            Current Rank:
            <span className="v2-bg-[#F9FAFB] v2-rounded-xs v2-py-0.5 v2-px-2 v2-border v2-text-[#344054] v2-ml-1 v2-text-[10px] md:v2-text-xs v2-flex v2-justify-center v2-items-center">
              {data?.data?.careerRank ?? ''}
            </span>
          </div>
        </div>
        <hr className=" v2-my-5" />

        <div className="v2-flex v2-justify-between v2-items-center v2-mb-5 md:v2-flex-row v2-flex-col">
          <CustomSelect
            valueKey={'value'}
            displayKey={'name'}
            value={selectedYear}
            placeholder="Select Year"
            leftIcon={calendarIcon}
            leftIconClasssName="!v2-top-[10px]"
            onChange={value => {
              setSelectedYear(value);
            }}
            showOptionTick={false}
            variant="boxed"
            className="!v2-rounded-xs after:!v2-hidden after:!v2-right-2 !v2-py-[9px] v2-text-[#667085]"
            wrapperClassName="v2-min-w-full md:v2-min-w-[130px] md:!v2-w-fit"
            options={getYears()}
          />

          <div className="v2-flex v2-items-center v2-justify-end v2-border v2-rounded-xs md:v2-w-fit v2-w-full">
            <Button
              type="button"
              scheme={activeButton === MONTHLY ? 'primary' : 'text'}
              variant="filled"
              className={cn(
                'v2-w-full md:v2-w-[110px] v2-h-8 v2-rounded-xs !v2-text-xs !v2-font-sweet-sans-pro !v2-p-0 v2-flex v2-justify-center v2-items-center v2-shadow-commissions-toggle-btn',
                { '!v2-text-[#154734] !v2-shadow-none': activeButton === BI_MONTHLY }
              )}
              onClick={() => {
                setActiveButton(MONTHLY);
              }}
              btnSize="md"
            >
              Monthly
            </Button>
            <Button
              type="button"
              scheme={activeButton === BI_MONTHLY ? 'primary' : 'text'}
              variant="filled"
              className={cn(
                'v2-w-full md:v2-w-[110px] v2-h-8 v2-rounded-xs !v2-text-xs !v2-font-sweet-sans-pro !v2-p-0 v2-flex v2-justify-center v2-items-center v2-shadow-commissions-toggle-btn',
                { '!v2-text-[#154734] !v2-shadow-none': activeButton === MONTHLY }
              )}
              onClick={() => {
                setActiveButton(BI_MONTHLY);
              }}
              btnSize="md"
            >
              Bi-Monthly
            </Button>
          </div>
        </div>
      </div>
      {isLoading ? (
        <div className="v2-h-[296px] v2-flex v2-justify-center v2-items-center">
          <ReportsLoader />
        </div>
      ) : (
        <>
          {data?.data && Array.isArray(chartData.uData) && chartData.uData.some(v => v !== 0) ? (
            <>
              <LineChart
                uData={chartData.uData ?? []}
                xLabels={Array.from(chartData.xLabels) ?? []}
                pData={chartData?.pData ?? []}
                isBiMonthly={activeButton === BI_MONTHLY}
                listOfMonthsWithRunerId={chartData.monthsWithRunIds}
                yAxisMax={chartData.yAxisMax}
                ySteps={chartData.yStep}
              />
              <div className="v2-flex v2-flex-col v2-gap-2 md:v2-flex-row md:v2-justify-between md:v2-items-center v2-px-4 md:v2-px-6">
                {activeButton === BI_MONTHLY && (
                  <div className="v2-flex v2-gap-[10px]">
                    <div className="v2-flex v2-gap-2 v2-items-center">
                      <div className="v2-h-3 v2-w-3 v2-bg-[#154734]" />
                      <p className="v2-font-sweet-sans-pro v2-text-sm v2-text-[#101828]">Bi-Monthly 1</p>
                    </div>
                    <div className="v2-flex v2-gap-2 v2-items-center">
                      <div className="v2-h-3 v2-w-3 v2-bg-[#F79009]" />
                      <p className="v2-font-sweet-sans-pro v2-text-sm v2-text-[#101828]">Bi-Monthly 2</p>
                    </div>
                  </div>
                )}

                <Link
                  href={'/commissions'}
                  className="v2-flex v2-text-base v2-text-[#154734] v2-font-sweet-sans-pro v2-font-normal v2-justify-end md:v2-ml-auto"
                >
                  View Current Earnings
                  <Image src={leftArrowIcon} alt="" />
                </Link>
              </div>
            </>
          ) : (
            <div className="v2-flex v2-justify-center v2-items-center v2-h-[276px] v2-w-full v2-px-4 md:v2-px-6">
              <div>
                <Image src={noData} alt="no commissions data" className="v2-mx-auto v2-mb-[10px]" />
                <p className="v2-font-sweet-sans-pro-medium v2-text-xs v2-text-[#101828]">No Commissions Data</p>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Commissions;
